import React from 'react';
import { Form, InputNumber } from 'antd';
import type { Rule } from 'antd/es/form';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../../../utils/transportCostAllocation';

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

// Mirrors of the backend bounds in productService.js (normalizeClearableNumber).
// The backend stays the validator of record; these rules keep an invalid
// entry visible with an inline Polish error instead of clamping it.
export const MAX_DIMENSION_MM = 99999999.99;
export const MAX_WEIGHT_KG = 9999999.999;
export const MAX_AVERAGE_COST_PLN = 99999999.9999;

const isEmpty = (value: unknown) => value === null || value === undefined || value === '';

export function positiveNumberRule(t: TranslateFn, max: number): Rule {
  return {
    validator: (_: unknown, value: unknown) => {
      if (isEmpty(value)) return Promise.resolve();
      const numeric = Number(value);
      return Number.isFinite(numeric) && numeric > 0 && numeric <= max
        ? Promise.resolve()
        : Promise.reject(new Error(t('products:card.validation.positive', { max })));
    },
  };
}

export function nonNegativeNumberRule(t: TranslateFn, max: number): Rule {
  return {
    validator: (_: unknown, value: unknown) => {
      if (isEmpty(value)) return Promise.resolve();
      const numeric = Number(value);
      return Number.isFinite(numeric) && numeric >= 0 && numeric <= max
        ? Promise.resolve()
        : Promise.reject(new Error(t('products:card.validation.nonNegative', { max })));
    },
  };
}

export function integerRangeRule(t: TranslateFn, min: number, max: number): Rule {
  return {
    validator: (_: unknown, value: unknown) => {
      if (isEmpty(value)) return Promise.resolve();
      const numeric = Number(value);
      return Number.isInteger(numeric) && numeric >= min && numeric <= max
        ? Promise.resolve()
        : Promise.reject(new Error(t('products:card.validation.integerRange', { min, max })));
    },
  };
}

interface CardNumberFieldProps {
  name: string | Array<string | number>;
  label: React.ReactNode;
  suffix?: React.ReactNode;
  rules?: Rule[];
  disabled?: boolean;
  placeholder?: string;
  extra?: React.ReactNode;
  className?: string;
}

/**
 * Numeric card field: vertical label, 40 px control, unit suffix, Polish
 * decimal comma. Deliberately no `min`/`max` on the InputNumber (see
 * .claude/rules/frontend-pages.md): rules block saving instead of coercing.
 */
export const CardNumberField: React.FC<CardNumberFieldProps> = ({
  name, label, suffix, rules, disabled, placeholder, extra, className,
}) => (
  <Form.Item name={name} label={label} rules={rules} extra={extra} className={className}>
    <InputNumber
      className="product-card-number"
      disabled={disabled}
      placeholder={placeholder}
      suffix={suffix}
      controls={false}
      {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
    />
  </Form.Item>
);
